/**
 * Static plan cards + feature matrix (USD, all branches).
 * Euro→USD ≈ 1.08. AI Receptionist from PKR 5,300 ≈ $19.
 */
import { RESTRO_REGISTER_URL } from '@/config/portal'
import { ALL_MODULES_TOTAL_USD } from '@/lib/pricingCatalog'

export type PlanId = 'pos_core' | 'full_stack' | 'addons'

export const PRICING_SECTION_TITLE = 'Simple monthly pricing for every branch'
export const PRICING_SECTION_SUB =
  'One price covers all your branches. Mix modules with the calculator below — or start with a ready stack.'

export const COMPARISON_TITLE = 'Full feature comparison'
export const COMPARISON_SUB = 'What you get on each ready-made stack.'

export type TierCard = {
  id: PlanId
  name: string
  tagline: string
  priceMain: string
  priceSub?: string
  priceNote?: string
  cta: string
  ctaHref: string
  featured?: boolean
  features: string[]
  badge?: string
}

export const TIER_CARDS: TierCard[] = [
  {
    id: 'pos_core',
    name: 'POS Core',
    tagline: 'Order taking, day sessions, reports',
    priceMain: '$11',
    priceSub: '/mo — all branches',
    priceNote: 'Required base module',
    cta: 'Start free trial',
    ctaHref: RESTRO_REGISTER_URL,
    features: [
      'POS terminal',
      'Order management',
      'Day sessions & cash counting',
      'Sales reports',
      'Staff & roles',
    ],
  },
  {
    id: 'full_stack',
    name: 'Full Stack',
    tagline: 'POS + KDS + Website + Riders + Inventory + Accounting',
    priceMain: '$71',
    priceSub: '/mo — all branches',
    priceNote: 'Six modules in one ready stack',
    cta: 'Start free trial',
    ctaHref: RESTRO_REGISTER_URL,
    featured: true,
    badge: 'Most popular',
    features: [
      'Everything in POS Core',
      'Kitchen Display ($5)',
      'Website + online ordering',
      'Rider app',
      'Inventory + recipes',
      'Full accounting + PO/GRN',
    ],
  },
  {
    id: 'addons',
    name: 'Add-on modules',
    tagline: 'Mix only what you need',
    priceMain: 'From $5',
    priceSub: '/mo per module — all branches',
    priceNote: `AI Receptionist + Assistant $19 each · Full set $${ALL_MODULES_TOTAL_USD}/mo`,
    cta: 'Build your stack',
    ctaHref: '#pricing-calculator',
    features: [
      'KDS / Waiter / Reservations — $5',
      'Website / Analytics / Riders / Stock — $11',
      'Accounting — $22',
      'AI Receptionist (WhatsApp) — $19 · no trial',
      'AI Assistant (Farori) — $19',
      `Full set — $${ALL_MODULES_TOTAL_USD}/mo`,
    ],
  },
]

export type ComparisonRow = {
  feature: string
  pos_core: boolean
  full_stack: boolean
  addons: boolean
}

export const COMPARISON_ROWS: ComparisonRow[] = [
  { feature: 'Cloud POS & terminals', pos_core: true, full_stack: true, addons: true },
  { feature: 'Kitchen display (KDS)', pos_core: false, full_stack: true, addons: true },
  { feature: 'Riders app + live tracking', pos_core: false, full_stack: true, addons: true },
  { feature: 'Inventory & recipes', pos_core: false, full_stack: true, addons: true },
  { feature: 'Accounting + PO/GRN', pos_core: false, full_stack: true, addons: true },
  { feature: 'Website + online ordering', pos_core: false, full_stack: true, addons: true },
  { feature: 'Reservations & waiter tools', pos_core: false, full_stack: false, addons: true },
  { feature: 'Website analytics', pos_core: false, full_stack: false, addons: true },
  { feature: 'AI Receptionist (WhatsApp)', pos_core: false, full_stack: false, addons: true },
  { feature: 'AI Assistant (Farori)', pos_core: false, full_stack: false, addons: true },
  { feature: 'Role-based staff access', pos_core: true, full_stack: true, addons: true },
  { feature: 'All branches included', pos_core: true, full_stack: true, addons: true },
]
